// Call pill state model (CU-869fk1hpz). Pure functions only, no React, MUI or
// redux, so Jest can load it without the components barrel.
//
// Input: the backend's realtime `voice.call` payloads (re-broadcast by
// LiveNotificationHandler as VOICE_CALL_WINDOW_EVENT) and the rows of the
// live-calls endpoint. Both are folded into one record per call_id. The UI
// shows one pill for the most important call, with a "+N" count for the rest.
//
import { formatUkPhone } from '../../utils/ukPhone';

// Payload: contract B2 v1 (~/pm/contracts/B2.md, 2026-10-23). Every event
// carries the same keys; `state` + `occurred_at` are the truth because events
// can arrive out of order:
//   { type: 'voice.call', version: 2, call_id, event, occurred_at,
//     state: 'ringing'|'answered'|'held'|'ended', outcome, direction, from, to,
//     site_number, line_name, ring_group, caller_label (null until B4),
//     lead: {id, name} | null, lead_summary?, answered_by: {practice_user_id,
//     name, is_you} | null, started_at, answered_at, ended_at, missed_reason,
//     ended_reason ('' | 'stale'), recipient_reason, next_actions? }
// Only `version >= 2` payloads drive the pill. The older backend sends
// `call.answered` to everyone and never sends `call.ended`, so its events stay
// with the inbound toast (see isPillPayload / LiveNotificationHandler).

export const CALL_STATE = Object.freeze({
    RINGING: 'ringing',
    ON_CALL: 'on_call',
    ANSWERED_ELSEWHERE: 'answered_elsewhere',
    HELD: 'held',
    ENDED: 'ended',
    MISSED: 'missed',
});

// How long a call stays on screen, counted from when THIS browser learned of
// the change (server clocks and queue lag must not eat into it).
export const ENDED_TTL_MS = 60 * 1000;          // "Log outcome", then closes
export const MISSED_TTL_MS = 2 * 60 * 1000;     // red, "Call back"
export const ELSEWHERE_TTL_MS = 4 * 1000;       // the old fade-out; now only the floor (see CLAIM_WINDOW_MS)
// A desk-phone call answered at another desk stays on screen as a compact "Answered at <desk>"
// pill with "I'm on this call", so the person who is really on it can name themselves (the desk
// holder is already named by the stronger desk source). It goes when the call ends, and at the
// latest this long after it was answered.
export const CLAIM_WINDOW_MS = 10 * 60 * 1000;
// The backend closes a call stuck ringing after 2 minutes; if that close is
// lost, the client drops it shortly after rather than ringing forever.
export const RINGING_STALE_MS = 2.5 * 60 * 1000;
// The live-calls endpoint only lists answered calls started < 4 h ago. The
// hook re-reads it every LIVE_RECHECK_MS while a call is live, so a lost end
// event closes the pill within a minute; this is only the last backstop.
export const LIVE_STALE_MS = 4 * 60 * 60 * 1000;
// A closed call is remembered this long so late or relayed events for it
// cannot reopen it.
export const TOMBSTONE_TTL_MS = 15 * 60 * 1000;

const TERMINAL = new Set([CALL_STATE.ENDED, CALL_STATE.MISSED]);
const LIVE = new Set([CALL_STATE.ON_CALL, CALL_STATE.HELD]);
// Events never move a call backwards: a late `ringing` after `answered`, or
// anything after `ended`, is ignored.
const RANK = {
    [CALL_STATE.RINGING]: 0,
    [CALL_STATE.ON_CALL]: 1,
    [CALL_STATE.HELD]: 1,
    [CALL_STATE.ANSWERED_ELSEWHERE]: 1,
    [CALL_STATE.ENDED]: 2,
    [CALL_STATE.MISSED]: 2,
};

export const isTerminal = (state) => TERMINAL.has(state);

const toMs = (value) => {
    if (!value) return null;
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? null : t;
};

const nameOf = (u) => (u && typeof u === 'object' ? (u.name || '') : '');

// Some publishers wrap the event ({ data: {...} } or a JSON string).
export const unwrapPayload = (data) => {
    if (!data) return null;
    let p = data;
    if (typeof p === 'string') {
        try { p = JSON.parse(p); } catch { return null; }
    }
    if (!p || typeof p !== 'object') return null;
    if (p.data && typeof p.data === 'object' && p.call_id == null && p.data.call_id != null) p = { ...p, ...p.data };
    return p.call_id != null ? p : null;
};

// True for payloads the pill handles (contract B2, version 2+).
export const isPillPayload = (raw) => {
    const p = unwrapPayload(raw);
    return Boolean(p && Number(p.version) >= 2 && p.state);
};

const eventKey = (p) => String(p.event || '').replace(/^call\./, '');

// Whether this user is the one on the call. `answered_by.is_you` decides
// (practice_user_id is not the login user id, so ids are never compared).
// Without it, `call.answered` is only ever sent to the answerer and
// `call.answered_elsewhere` to everyone else; outbound calls are the dialler's.
const isMine = (p) => {
    // A handset this user shares: the phone system only ever names the desk, so
    // `is_you` is false for everyone on it. The call is still theirs -- it is on their
    // phone -- and without this it shows as "answered elsewhere" and fades in four
    // seconds, taking Hold, Transfer and End with it.
    if (p.recipient_reason === 'shared') return true;
    if (p.answered_by && typeof p.answered_by.is_you === 'boolean') return p.answered_by.is_you;
    // A DentalSEM staff observer copy is never "my" call.
    if (p.recipient_reason === 'superuser') return false;
    const key = eventKey(p);
    if (key === 'answered_elsewhere') return false;
    if (key === 'answered' || key === 'held' || key === 'unheld') return true;
    return p.direction === 'outbound';
};

// Which pill state a payload means for THIS user, or null to ignore it.
export const stateFromPayload = (p) => {
    const key = eventKey(p);
    const state = String(p.state || '');
    const mine = isMine(p);
    if (state === 'ended' || key === 'ended' || key === 'missed') {
        // Nobody picking up my own dial is "No answer" (an ended call), not a
        // missed call to ring back.
        if (p.direction === 'outbound') return CALL_STATE.ENDED;
        const missed = p.outcome === 'missed' || key === 'missed' || (!p.answered_at && !p.answered_by);
        return missed ? CALL_STATE.MISSED : CALL_STATE.ENDED;
    }
    if (state === 'held' || p.held === true) return mine ? CALL_STATE.HELD : CALL_STATE.ANSWERED_ELSEWHERE;
    if (state === 'answered') return mine ? CALL_STATE.ON_CALL : CALL_STATE.ANSWERED_ELSEWHERE;
    if (state === 'ringing') return CALL_STATE.RINGING;
    return null;
};

// A closed call becomes a tombstone: invisible, but it keeps the newest
// occurred_at seen so older messages can't reopen it. `final` tombstones
// (finished or dismissed calls) ignore everything.
export const tombstone = (prev, callId, { final = false, occurredAt = null, now = Date.now() } = {}) => {
    const id = String(callId);
    const existing = prev?.[id];
    if (existing?.closed && (existing.final || !final)) return prev;
    return {
        ...(prev || {}),
        [id]: { callId: id, closed: true, final, occurredAt: occurredAt ?? existing?.occurredAt ?? null, closedAt: now },
    };
};

export const liveCalls = (calls) => Object.values(calls || {}).filter((c) => !c.closed);

// Fold one payload into the existing record (or create it). Returns `prev`
// itself when the payload changes nothing.
export const applyCallEvent = (prev, raw, { now = Date.now() } = {}) => {
    const p = unwrapPayload(raw);
    if (!p || p.is_test || !isPillPayload(p)) return prev;
    const state = stateFromPayload(p);
    if (!state) return prev;
    const callId = String(p.call_id);
    const existing = prev?.[callId];
    const occurredAt = toMs(p.occurred_at);
    if (existing?.closed) {
        // Only a genuinely newer message (e.g. a transfer to me after the
        // "answered elsewhere" fade) may reopen a closed call, never a ringing.
        if (existing.final || state === CALL_STATE.RINGING) return prev;
        if (!occurredAt || !existing.occurredAt || occurredAt <= existing.occurredAt) return prev;
    } else if (existing) {
        if (isTerminal(existing.state)) return prev;
        if (RANK[state] < RANK[existing.state]) return prev;
        // Same rank (held <-> unheld, transfers): the newer message wins.
        if (RANK[state] === RANK[existing.state] && occurredAt && existing.occurredAt && occurredAt < existing.occurredAt) return prev;
    }
    const live = existing && !existing.closed ? existing : null;
    // The 2-minute ringing sweeper: just close the pill.
    if (p.ended_reason === 'stale') return tombstone(prev, callId, { final: true, occurredAt, now });
    // "Call ended" (with Log outcome) is for whoever was on the call. Everyone
    // else in the ringing audience also gets `call.ended`: close their pill.
    if (state === CALL_STATE.ENDED && !(live?.mine || isMine(p))) return tombstone(prev, callId, { final: true, occurredAt, now });
    const base = live || { callId, firstSeenAt: now };
    const answeredBy = p.answered_by !== undefined ? p.answered_by : (base.answeredBy ?? null);
    const direction = p.direction || base.direction || 'inbound';
    const record = {
        ...base,
        state,
        mine: Boolean(base.mine || LIVE.has(state) || (direction === 'outbound' && p.recipient_reason !== 'superuser')),
        recipientReason: p.recipient_reason || base.recipientReason || '',
        direction,
        from: p.from ?? base.from ?? '',
        to: p.to ?? base.to ?? '',
        callerIdWithheld: Boolean(p.caller_id_withheld ?? base.callerIdWithheld),
        siteNumber: p.site_number ?? base.siteNumber ?? '',
        lineName: p.line_name ?? base.lineName ?? '',
        ringGroup: p.ring_group ?? base.ringGroup ?? '',
        callerLabel: p.caller_label ?? base.callerLabel ?? '',
        // Desks ringing for this call ("R TCO 1"), sent while it rings (CU-869fmcc9r).
        ringingAt: Array.isArray(p.ringing_at) ? p.ringing_at.filter((n) => typeof n === 'string' && n) : (base.ringingAt ?? []),
        lead: p.lead !== undefined ? p.lead : (base.lead ?? null),
        leadSummary: p.lead_summary ?? base.leadSummary ?? null,
        answeredBy,
        answeredByName: nameOf(answeredBy) || base.answeredByName || '',
        // The desk they answered at ("R TCO 1"), when the backend knows it (CU-869fmcc9r).
        answeredByDesk: (answeredBy && typeof answeredBy === 'object' ? answeredBy.desk : '') || base.answeredByDesk || '',
        notLinked: p.recipient_reason ? p.recipient_reason === 'not_linked' : Boolean(base.notLinked),
        nextActions: Array.isArray(p.next_actions) ? p.next_actions : (base.nextActions ?? null),
        noAnswer: state === CALL_STATE.ENDED && direction === 'outbound' && !p.answered_at && !base.answeredAt,
        startedAt: toMs(p.started_at) || base.startedAt || now,
        answeredAt: toMs(p.answered_at) || base.answeredAt
            || (state !== CALL_STATE.RINGING && !isTerminal(state) ? now : null),
        // Local time the call finished, for the 60 s / 2 min on screen.
        endedAt: isTerminal(state) ? now : null,
        occurredAt: Math.max(occurredAt || 0, base.occurredAt || 0) || null,
        updatedAt: now,
    };
    return { ...(prev || {}), [callId]: record };
};

// After a refresh (or a long time away) the live-calls endpoint is the truth
// for calls still in progress. Live records it no longer lists have ended
// while we weren't listening, except ones updated after the request started
// (an event that raced the response). Finished records and tombstones stay.
export const reconcileLiveCalls = (prev, rows, { now = Date.now(), requestedAt = -Infinity } = {}) => {
    let next = {};
    // The endpoint lists an answered call only to whoever answered it, so a bystander's
    // "answered elsewhere" record is never in `rows`: it must not be dropped for that. It stays
    // until its claim window ends (expireCalls) or `call.ended` closes it.
    const holdsClaim = (c) => c.state === CALL_STATE.ANSWERED_ELSEWHERE && (c.answeredAt || c.updatedAt) + CLAIM_WINDOW_MS > now;
    Object.values(prev || {}).forEach((c) => {
        if (c.closed || isTerminal(c.state) || c.updatedAt >= requestedAt || holdsClaim(c)) next[c.callId] = c;
        else next = tombstone(next, c.callId, { occurredAt: c.occurredAt, now });
    });
    // Every call the endpoint lists is still in progress, whatever its row's
    // occurred_at says (the endpoint stamps answered_at/started_at, which is
    // older than a hold or transfer we already applied).
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const p = unwrapPayload(row);
        if (!p) return;
        const id = String(p.call_id);
        const existing = prev?.[id];
        if (existing?.closed && existing.final) return;
        if (existing && !existing.closed) {
            // Newer row: take it; older row: keep what the events told us.
            const merged = applyCallEvent({ [id]: existing }, p, { now });
            next = { ...next, [id]: merged[id] && !merged[id].closed ? merged[id] : existing };
            return;
        }
        const created = applyCallEvent({}, p, { now });
        if (created[id] && !created[id].closed) next = { ...next, [id]: created[id] };
    });
    return next;
};

// Another tab's calls, merged record by record: the further-along state wins,
// then the newer occurred_at; tombstones win over the record they closed.
export const mergeCalls = (prev, theirs) => {
    let next = prev || {};
    Object.values(theirs || {}).forEach((t) => {
        if (!t?.callId) return;
        const mine = next[t.callId];
        let take = false;
        if (!mine) take = true;
        else if (mine.closed && mine.final) take = false;
        else if (t.closed) take = Boolean(t.final) || !mine.occurredAt || (t.occurredAt || 0) >= mine.occurredAt;
        else if (mine.closed) take = Boolean(t.occurredAt && mine.occurredAt && t.occurredAt > mine.occurredAt);
        else if (RANK[t.state] !== RANK[mine.state]) take = RANK[t.state] > RANK[mine.state];
        else take = (t.occurredAt || 0) > (mine.occurredAt || 0);
        if (take) next = { ...next, [t.callId]: t };
    });
    return next;
};

const expiresAt = (c) => {
    if (c.closed) return c.closedAt + TOMBSTONE_TTL_MS;
    if (c.state === CALL_STATE.ENDED) return (c.endedAt || c.updatedAt) + ENDED_TTL_MS;
    if (c.state === CALL_STATE.MISSED) return (c.endedAt || c.updatedAt) + MISSED_TTL_MS;
    if (c.state === CALL_STATE.ANSWERED_ELSEWHERE) return Math.max(c.updatedAt + ELSEWHERE_TTL_MS, (c.answeredAt || c.updatedAt) + CLAIM_WINDOW_MS);
    if (c.state === CALL_STATE.RINGING) return c.updatedAt + RINGING_STALE_MS;
    return c.updatedAt + LIVE_STALE_MS;
};

// Close calls whose time on screen is over (they become tombstones) and
// forget old tombstones. Returns prev unchanged when nothing changed.
export const expireCalls = (prev, now = Date.now()) => {
    let next = prev;
    Object.values(prev || {}).forEach((c) => {
        if (expiresAt(c) > now) return;
        if (c.closed) {
            next = { ...next };
            delete next[c.callId];
        } else {
            next = tombstone(next, c.callId, { final: isTerminal(c.state), occurredAt: c.occurredAt, now });
        }
    });
    return next;
};

// Milliseconds until the next record would expire, so the hook can schedule
// one timer instead of polling. null when nothing will expire.
export const nextExpiryIn = (calls, now = Date.now()) => {
    let min = null;
    Object.values(calls || {}).forEach((c) => {
        const wait = Math.max(0, expiresAt(c) - now + 50);
        if (min == null || wait < min) min = wait;
    });
    return min;
};

// Which calls get the pill first: my own live call, then the newest ringing,
// then missed, ended, answered elsewhere.
const PRIORITY = {
    [CALL_STATE.ON_CALL]: 0,
    [CALL_STATE.HELD]: 0,
    [CALL_STATE.RINGING]: 1,
    [CALL_STATE.MISSED]: 2,
    [CALL_STATE.ENDED]: 3,
    [CALL_STATE.ANSWERED_ELSEWHERE]: 4,
};
export const sortCalls = (calls) => liveCalls(calls)
    .sort((a, b) => (PRIORITY[a.state] - PRIORITY[b.state]) || (b.updatedAt - a.updatedAt));

// Do not disturb hides everything except this user's own calls (live, or
// just ended and waiting for "Log outcome").
const suppressedByDnd = (c) => !(LIVE.has(c.state) || (c.mine && c.state === CALL_STATE.ENDED));

// What to render: the pill's call, how many others are visible, and how many
// are tucked into the dot (hidden by the user or by Do not disturb).
export const selectView = (calls, { hidden = [], dnd = false, focusedId = null } = {}) => {
    const hiddenSet = new Set((hidden || []).map(String));
    const visible = [];
    const tucked = [];
    sortCalls(calls).forEach((c) => {
        if (hiddenSet.has(c.callId) || (dnd && suppressedByDnd(c))) tucked.push(c);
        else visible.push(c);
    });
    // The "+N" button on the pill picks which visible call it shows (CU-869fmc59b): that
    // call moves to the front, the rest keep their order behind it. An id that is no
    // longer visible is ignored, so the usual priority order stands.
    const at = focusedId == null ? -1 : visible.findIndex((c) => c.callId === String(focusedId));
    const ordered = at > 0 ? [visible[at], ...visible.filter((_, index) => index !== at)] : visible;
    // Answered-elsewhere calls only fade out; they never count towards the dot.
    const dotCount = tucked.filter((c) => c.state !== CALL_STATE.ANSWERED_ELSEWHERE).length;
    return { primary: ordered[0] || null, others: Math.max(0, ordered.length - 1), dotCount, visible: ordered, tucked };
};

// True while any call is still in progress (the hook re-checks the endpoint).
export const hasLiveCall = (calls) => liveCalls(calls).some((c) => !isTerminal(c.state));

// The other party's number: the caller for inbound, the number dialled for
// outbound.
export const otherParty = (c) => (c.direction === 'outbound' ? c.to : c.from) || '';

export const callerDisplay = (c) => {
    const raw = otherParty(c);
    // Staff read and say UK numbers as 0113 496 0199, never +44 (CU-869fmcc9r).
    const number = c.callerIdWithheld || !raw ? 'Withheld number' : formatUkPhone(raw);
    // caller_label is null until CU B4 fills it; then the name or number alone.
    return { name: c.lead?.name || '', number, label: c.callerLabel || '' };
};

// Set-up words staff shouldn't see on a live call ("Simulator", "Elevate test
// line"): admins still do, as a hint about which line it was.
const INTERNAL_LINE = /\b(simulator|test line)\b/i;

export const lineDisplay = (c, { isAdmin = false } = {}) => {
    const line = c.lineName || c.ringGroup || '';
    if (line && (isAdmin || !INTERNAL_LINE.test(line))) return line;
    return c.siteNumber ? formatUkPhone(c.siteNumber) : '';
};

export const STATE_LABEL = {
    [CALL_STATE.RINGING]: 'Ringing',
    [CALL_STATE.ON_CALL]: 'On call',
    [CALL_STATE.ANSWERED_ELSEWHERE]: 'Answered',
    [CALL_STATE.HELD]: 'On hold',
    [CALL_STATE.ENDED]: 'Call ended',
    [CALL_STATE.MISSED]: 'Missed call',
};

// Where an inbound call is ringing for this user. The backend sends a call to
// whoever holds the desk that rang (recipient_reason 'desk'); there is no Answer
// button in the CRM, so the line says to pick up the handset.
export const ringingWhere = (c, { deskName = '', isAdmin = false } = {}) => {
    if (c.recipientReason === 'desk') {
        return deskName ? `Ringing at your desk (${deskName}) — pick up your phone` : 'Ringing at your desk — pick up your phone';
    }
    const desks = (c.ringingAt || []).filter((n) => isAdmin || !INTERNAL_LINE.test(n));
    // A handset this user shares: anyone on the team can take it.
    if (c.recipientReason === 'shared') {
        return desks.length === 1 ? `Ringing at ${desks[0]} — pick up there` : 'Ringing at one of your handsets — pick up there';
    }
    // Nobody at the desk, and it's this user's usual desk (CU-869fmd1vf).
    if (c.recipientReason === 'default_desk') {
        return desks.length === 1 ? `Ringing at ${desks[0]}, your usual desk — pick up there` : 'Ringing at your usual desk — pick up there';
    }
    // One desk: name it. Several: the ring group ("Reception"), else the first desk +N.
    let where = desks.length === 1 ? desks[0] : '';
    if (!where) where = c.ringGroup && (isAdmin || !INTERNAL_LINE.test(c.ringGroup)) ? c.ringGroup : '';
    if (!where && desks.length > 1) where = `${desks[0]} +${desks.length - 1}`;
    if (!where) where = lineDisplay(c, { isAdmin });
    return where ? `Ringing at ${where}` : 'Ringing';
};

// "Sam (R TCO 1)", "Sam", or the desk alone.
export const answererDisplay = (c) => {
    const name = c.answeredByName || '';
    const desk = c.answeredByDesk || '';
    if (name && desk && name !== desk) return `${name} (${desk})`;
    return name || desk;
};

// The status line under the caller: "Ringing at your desk (R TCO 2) — pick up
// your phone", "Ringing at Reception", "On call · you", "Answered by Sam (R TCO 1)",
// "Missed call · Reception". Options: the desk this user holds, and whether
// internal line names may show (admins).
export const statusLine = (c, { deskName = '', isAdmin = false } = {}) => {
    const line = lineDisplay(c, { isAdmin });
    if (c.state === CALL_STATE.ANSWERED_ELSEWHERE) {
        const who = answererDisplay(c);
        return who ? `Answered by ${who}` : 'Answered elsewhere';
    }
    if (c.state === CALL_STATE.RINGING && c.direction !== 'outbound') return ringingWhere(c, { deskName, isAdmin });
    if ((c.state === CALL_STATE.ON_CALL || c.state === CALL_STATE.HELD) && c.mine) {
        // On a shared handset there is no "you": say which phone it is on, because any
        // of the team could be the one holding it.
        if (c.recipientReason === 'shared') {
            return [STATE_LABEL[c.state], answererDisplay(c) || 'a shared handset'].join(' · ');
        }
        return `${STATE_LABEL[c.state]} · you`;
    }
    const who = (c.state === CALL_STATE.ON_CALL || c.state === CALL_STATE.HELD) ? answererDisplay(c) : '';
    let label = STATE_LABEL[c.state];
    // My own dial: Elevate rings my desk first, then the number.
    if (c.state === CALL_STATE.RINGING) label = 'Calling';
    if (c.noAnswer) label = 'No answer';
    return [label, who || line].filter(Boolean).join(' · ');
};

export const formatDuration = (ms) => {
    const total = Math.max(0, Math.floor((ms || 0) / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const mm = h ? String(m).padStart(2, '0') : String(m);
    return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
};

// Timer shown on live calls: since answered (on call / held), since started
// while ringing. Ended/missed show no timer.
export const timerStart = (c) => {
    if (c.state === CALL_STATE.ON_CALL || c.state === CALL_STATE.HELD) return c.answeredAt || c.startedAt;
    if (c.state === CALL_STATE.RINGING) return c.startedAt;
    return null;
};

// ── Buttons ──────────────────────────────────────────────────────────────
// The backend sends `next_actions` (ids, or {id, label}); unknown ids are
// ignored so a newer backend never renders a dead button. Without
// next_actions the pill derives the same buttons from the state.
export const ACTION = Object.freeze({
    OPEN_PATIENT: 'open_patient',
    FIND_PATIENT: 'find_or_create_patient',
    // Call Logs v2 piece 4 (CU-869fmbgfm): Create lead opens the call's lead
    // pop-up pre-filled; Script opens the existing call-script pop-up.
    CREATE_LEAD: 'create_lead',
    SCRIPT: 'script',
    LOG_OUTCOME: 'log_outcome',
    CALL_BACK: 'call_back',
});

const ACTION_ALIASES = {
    open_patient: ACTION.OPEN_PATIENT,
    patient: ACTION.OPEN_PATIENT,
    open_lead: ACTION.OPEN_PATIENT,
    find_or_create_patient: ACTION.FIND_PATIENT,
    create_patient: ACTION.CREATE_LEAD,
    create_lead: ACTION.CREATE_LEAD,
    find_patient: ACTION.FIND_PATIENT,
    script: ACTION.SCRIPT,
    call_script: ACTION.SCRIPT,
    inbound_script: ACTION.SCRIPT,
    outbound_script: ACTION.SCRIPT,
    log_outcome: ACTION.LOG_OUTCOME,
    call_back: ACTION.CALL_BACK,
    callback: ACTION.CALL_BACK,
};

const DEFAULT_LABEL = {
    [ACTION.OPEN_PATIENT]: 'Open patient',
    [ACTION.CREATE_LEAD]: 'Create lead',
    [ACTION.LOG_OUTCOME]: 'Log outcome',
    [ACTION.CALL_BACK]: 'Call back',
};

const LIVE_STATES = new Set([CALL_STATE.RINGING, CALL_STATE.ON_CALL, CALL_STATE.HELD]);
export const isLiveCall = (c) => LIVE_STATES.has(c?.state);

// Which script the Script button opens (CU-869fmcc9r): a number matched to a lead gets
// that lead's script (the lead page's outbound one), whichever way the call goes;
// anyone else the inbound (general enquiry) script.
export const SCRIPT_KIND = Object.freeze({ LEAD: 'lead', INBOUND: 'inbound' });
export const scriptKind = (c) => (c?.lead?.id ? SCRIPT_KIND.LEAD : SCRIPT_KIND.INBOUND);
// One label whichever script opens (CU-869fmcc9r): staff don't need to know its category.
const SCRIPT_LABEL = 'Script';

// During a call (spec #pill): inbound unknown → Create lead · Script;
// inbound known → Open patient · Script; outbound to a lead →
// Script · Open patient; outbound unknown → Create lead · Script.
const liveActions = (c) => {
    if (c.direction === 'outbound') return c.lead?.id ? [ACTION.SCRIPT, ACTION.OPEN_PATIENT] : [ACTION.CREATE_LEAD, ACTION.SCRIPT];
    return [c.lead?.id ? ACTION.OPEN_PATIENT : ACTION.CREATE_LEAD, ACTION.SCRIPT];
};

const endedUnknownInbound = (c) => c.state === CALL_STATE.ENDED && c.direction !== 'outbound'
    && !c.lead?.id && Boolean(c.answeredAt) && !c.callerIdWithheld;

// The patient button for this caller: Open patient (known), Create lead (a number we
// don't know), none (withheld).
const patientAction = (c) => {
    if (c.lead?.id) return ACTION.OPEN_PATIENT;
    return !c.callerIdWithheld && otherParty(c) ? ACTION.CREATE_LEAD : null;
};

// After the call, the one main button: Call back, else Create lead, else Log outcome.
const AFTER_CALL_MAIN = [ACTION.CALL_BACK, ACTION.CREATE_LEAD, ACTION.LOG_OUTCOME];

const derivedActions = (c) => {
    if (isLiveCall(c)) return liveActions(c);
    // Spec #after (CU-869fmbgfn): an answered inbound call from an unknown
    // caller ends showing Create lead (for the ended pill's 60 s), no pop-up.
    if (endedUnknownInbound(c)) return [ACTION.CREATE_LEAD, ACTION.LOG_OUTCOME];
    // Known caller: Open patient. A number we don't know: Create lead, with linking
    // to an existing patient in the pill's menu. No "Find patient" (CU-869fmcc9r).
    const out = [ACTION.FIND_PATIENT];
    // My outbound calls get their outcome from the backend after the call
    // (auto-logged no-answer toast, or the one question): no Log outcome button.
    if (c.state === CALL_STATE.ENDED && c.direction !== 'outbound') out.push(ACTION.LOG_OUTCOME);
    if (c.state === CALL_STATE.MISSED && !c.callerIdWithheld && otherParty(c)) out.push(ACTION.CALL_BACK);
    return out;
};

export const actionsFor = (c) => {
    let source = Array.isArray(c.nextActions) ? c.nextActions : derivedActions(c);
    // Scripts live in the browser: a live call always offers one, whatever the
    // backend's next_actions say.
    if (isLiveCall(c) && Array.isArray(c.nextActions)) source = [...source, ACTION.SCRIPT];
    const seen = new Set();
    const list = source.reduce((acc, item) => {
        const rawId = typeof item === 'string' ? item : item?.id;
        let id = ACTION_ALIASES[String(rawId || '').toLowerCase()];
        // "Find or create patient" (also from the backend's next_actions) is shown as what it
        // does for this caller: Open patient, or Create lead for a number we don't know.
        let label = typeof item === 'object' ? item?.label : '';
        if (id === ACTION.FIND_PATIENT) {
            id = patientAction(c);
            label = '';
        }
        if (!id || seen.has(id)) return acc;
        // A patient button needs a patient; a call back needs a number.
        if (id === ACTION.OPEN_PATIENT && !c.lead?.id) return acc;
        if (id === ACTION.CALL_BACK && (c.callerIdWithheld || !otherParty(c))) return acc;
        seen.add(id);
        const fallback = id === ACTION.SCRIPT ? SCRIPT_LABEL : DEFAULT_LABEL[id];
        acc.push({ id, label: label || fallback });
        return acc;
    }, []);
    // One main button: the first one during a call; Call back / Log outcome after it.
    const main = isLiveCall(c) ? list[0]?.id : AFTER_CALL_MAIN.find((id) => list.some((a) => a.id === id));
    return list.map((a) => ({ ...a, primary: a.id === main }));
};

// "Link to existing patient" sits in the pill's menu for a caller we don't know
// but can identify by number (CU-869fmcc9r).
export const LINK_LEAD = 'link_lead';
export const canLinkCaller = (c) => Boolean(c) && !c.lead?.id && !c.callerIdWithheld && Boolean(otherParty(c))
    && c.state !== CALL_STATE.ANSWERED_ELSEWHERE;

// Desktop notification text for a ringing call: who, and where to pick up.
export const notificationFor = (c, { deskName = '' } = {}) => {
    const { name, number, label } = callerDisplay(c);
    return {
        title: name ? `Incoming call: ${name}` : 'Incoming call',
        body: [name ? number : [label, number].filter(Boolean).join(' · '), ringingWhere(c, { deskName })].filter(Boolean).join(' · '),
        tag: `call-pill-${c.callId}`,
    };
};

const UK_TIME = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/London',
});

// The Call Logs row shape the call flow (components/callFlow) reads, built from a live call
// (B2 call_id is the voice call's id, so the server pre-fill and the link work).
export const pillLeadRow = (c) => ({
    id: `voice:${c.callId}`,
    contact: c.callerIdWithheld ? '' : otherParty(c),
    direction: c.direction,
    callerLabel: c.callerLabel || '',
    siteLabel: lineDisplay(c),
    lead: c.lead?.id ? c.lead : null,
    timestampHuman: c.startedAt ? UK_TIME.format(new Date(c.startedAt)) : '',
    // Live payloads carry no caller label (shared number unknown): link this call only.
    linkEarlier: false,
});
